import * as React from 'react'
import { Laptop, Play, Trash2, Upload, Volume2, VolumeX } from 'lucide-react'
import { RUN_EVENTS, type RunEvent } from '@acs/shared'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/cn'
import {
  CUSTOM_VOICE_ID,
  FLEET_VOICE_ID,
  MAX_CUSTOM_SECONDS,
  VOICES,
  clearCustomSound,
  clearFleetSound,
  getCustomSoundName,
  getEnabled,
  getFleetSoundName,
  getReady,
  getVoice,
  getVolume,
  isCustomSoundReady,
  isFleetSoundReady,
  preview,
  setCustomSound,
  setEnabled,
  setFleetSound,
  setVoice,
  setVolume,
  subscribe,
} from '@/lib/sfx'
import { Note, SettingCard } from './controls'

/**
 * The Sounds section of the hunt settings page.
 *
 * Deliberately OUTSIDE the draft/Update cycle every other section lives in:
 * a change here applies the moment it is made and the footer never lights up
 * for it. The card says so, because a page whose every other control needs
 * Update pressing teaches you to press Update.
 *
 * Two kinds of upload live on each event's card, and the difference is the
 * whole design: "for every console" stores the file server-side and reaches
 * each open console over the stream within seconds, while "this browser only"
 * stays in this machine's IndexedDB and — being an explicit choice — wins
 * over the fleet's file here without affecting anybody else.
 *
 * Nothing on this panel touches the announce path's cost. An uploaded file is
 * decoded once — at upload here, at page load everywhere else; what
 * `playRunEvent` does when a shift opens is unchanged — a synchronous start
 * of samples already in memory.
 */

/** What each event means, in the operator's terms rather than the enum's. */
const EVENT_META: Readonly<Record<RunEvent, { title: string; subtitle: string }>> = {
  active: {
    title: 'Active hit',
    subtitle:
      'The sighting itself: a schedule reports ACTIVE and the wait begins — the Brazil burst arms, or the Current flow’s hold starts — roughly a minute before the claim can fire. The heads-up sound, heard wherever a wait is set.',
  },
  burst: {
    title: 'Claim fired',
    subtitle:
      'The wait is over and the claim — or the Brazil burst — goes out. With no wait or hold configured, this is also the sighting.',
  },
  miss: {
    title: 'Active hit missed',
    subtitle:
      'A sighting that did not convert: the claim was refused, or a stood-down watch saw the shift open without claiming it. Every row under Active hits is heard.',
  },
  won: {
    title: 'Shift won',
    subtitle: 'The seat is granted. Rare, and worth hearing from across the room.',
  },
}

const selectClass =
  'h-9 w-full rounded-md border border-line bg-s2 px-2.5 text-[13px] text-fg ' +
  'transition-colors duration-100 hover:border-line-strong ' +
  'focus:border-accent focus:ring-2 focus:ring-accent/25 focus:outline-none'

export function SoundsPanel() {
  const enabled = React.useSyncExternalStore(subscribe, getEnabled, () => false)
  const ready = React.useSyncExternalStore(subscribe, getReady, () => false)
  const volume = React.useSyncExternalStore(subscribe, getVolume, () => 0)

  return (
    <>
      <Note tone="info" quiet>
        Everything here applies the instant you change it — nothing waits on Update. An upload{' '}
        <strong>for every console</strong> is stored on the server and reaches each open console
        within seconds; the voice picker and volume stay <strong>this browser&rsquo;s own</strong>.
        Every file is decoded once and held in memory, so playback stays immediate however busy the
        fleet is.
      </Note>

      <SettingCard
        title="Console sound"
        subtitle="One switch and one level for everything the console plays. The same switch lives on the rail."
      >
        <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-5">
          <div className="min-w-0 flex-1">
            <Checkbox
              checked={enabled}
              onCheckedChange={setEnabled}
              label="Play a sound on claims, misses and wins"
            />
            {enabled && !ready ? (
              <p role="status" className="mt-1 text-[12px] text-warn">
                On, but the browser will not start audio until you click or type somewhere on the
                page. Your next click anywhere fixes it.
              </p>
            ) : null}
          </div>
          <div className="flex flex-none items-center gap-2 sm:w-[16rem]">
            {volume === 0 ? (
              <VolumeX aria-hidden="true" className="size-4 flex-none text-muted" />
            ) : (
              <Volume2 aria-hidden="true" className="size-4 flex-none text-muted" />
            )}
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              disabled={!enabled}
              aria-label="Sound volume"
              onChange={(e) => setVolume(Number(e.target.value))}
              className={cn('w-full accent-[var(--accent,currentColor)]', !enabled && 'opacity-40')}
            />
            <span className="tabular w-9 flex-none text-right text-[12px] text-muted">
              {Math.round(volume * 100)}%
            </span>
          </div>
        </div>
      </SettingCard>

      {RUN_EVENTS.map((event) => (
        <EventSoundCard key={event} event={event} enabled={enabled} />
      ))}
    </>
  )
}

/**
 * One event's sound: the built-in voices, the operator's own file, a preview.
 *
 * The three per-event snapshots are primitives (a string, a string-or-null, a
 * boolean), which is what lets `useSyncExternalStore` compare them by value —
 * an object snapshot rebuilt per read would re-render this card on every
 * store emit forever.
 */
function EventSoundCard({ event, enabled }: { event: RunEvent; enabled: boolean }) {
  const meta = EVENT_META[event]
  const chosen = React.useSyncExternalStore(subscribe, () => getVoice(event), () => '')
  const customName = React.useSyncExternalStore(
    subscribe,
    () => getCustomSoundName(event),
    () => null
  )
  const customReady = React.useSyncExternalStore(
    subscribe,
    () => isCustomSoundReady(event),
    () => false
  )
  const fleetName = React.useSyncExternalStore(
    subscribe,
    () => getFleetSoundName(event),
    () => null
  )
  const fleetReady = React.useSyncExternalStore(
    subscribe,
    () => isFleetSoundReady(event),
    () => false
  )

  const fileRef = React.useRef<HTMLInputElement>(null)
  /** Which destination the hidden file input is currently serving. */
  const modeRef = React.useRef<'fleet' | 'local'>('fleet')
  const [uploading, setUploading] = React.useState<'fleet' | 'local' | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const pick = (mode: 'fleet' | 'local') => {
    modeRef.current = mode
    fileRef.current?.click()
  }

  const onFile = async (file: File | undefined) => {
    if (!file) return
    const mode = modeRef.current
    setUploading(mode)
    setError(null)
    // Everything slow happens here, once — never when an event fires. The
    // fleet path also PUTs the file to the server, which is what makes every
    // other console play it.
    const failure =
      mode === 'fleet' ? await setFleetSound(event, file) : await setCustomSound(event, file)
    setUploading(null)
    setError(failure)
    if (failure === null) preview(event, mode === 'fleet' ? FLEET_VOICE_ID : CUSTOM_VOICE_ID)
  }

  const removeFleet = async () => {
    setError(null)
    const failure = await clearFleetSound(event)
    setError(failure)
  }

  return (
    <SettingCard title={meta.title} subtitle={meta.subtitle}>
      <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:gap-5">
        <div className="min-w-0 flex-1 py-0.5">
          <p className="text-[12px] leading-[1.5] text-muted">
            Pick a built-in voice, or upload a clip (up to {MAX_CUSTOM_SECONDS}s — MP3, WAV or
            OGG). An upload for <strong className="text-fg">every console</strong> plays for the
            whole fleet within seconds; one for{' '}
            <strong className="text-fg">this browser only</strong> overrides it here and touches
            nobody else. Either selects itself and plays once so you hear what you chose.
          </p>
          {fleetName !== null ? (
            <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
              <span className="min-w-0 truncate text-fg" title={fleetName}>
                Every console: <strong>{fleetName}</strong>
                {!fleetReady ? <span className="text-muted"> (loading…)</span> : null}
              </span>
              <Tooltip content="Remove it for every console" side="top">
                <Button
                  variant="ghost"
                  size="xs"
                  iconOnly
                  aria-label={`Remove the fleet-wide sound for “${meta.title}”`}
                  onClick={() => void removeFleet()}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </Tooltip>
            </p>
          ) : null}
          {customName !== null ? (
            <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
              <span className="min-w-0 truncate text-fg" title={customName}>
                This browser: <strong>{customName}</strong>
                {!customReady ? <span className="text-muted"> (loading…)</span> : null}
              </span>
              <Tooltip content="Remove it from this browser" side="top">
                <Button
                  variant="ghost"
                  size="xs"
                  iconOnly
                  aria-label={`Remove this browser’s sound for “${meta.title}”`}
                  onClick={() => clearCustomSound(event)}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </Tooltip>
            </p>
          ) : null}
          {error !== null ? (
            <p role="alert" className="mt-1.5 text-[12px] text-warn">
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex w-full flex-none items-center gap-1.5 sm:w-[22rem]">
          <select
            value={chosen}
            aria-label={`Sound for “${meta.title}”`}
            onChange={(e) => {
              setVoice(event, e.target.value)
              // Heard, not only saved — choosing a sound you cannot hear is
              // choosing blind, and this click is itself the gesture audio needs.
              preview(event, e.target.value)
            }}
            className={selectClass}
          >
            {VOICES[event].map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
            {fleetName !== null ? (
              <option value={FLEET_VOICE_ID}>Every console — {fleetName}</option>
            ) : null}
            {customName !== null ? (
              <option value={CUSTOM_VOICE_ID}>This browser — {customName}</option>
            ) : null}
          </select>

          <Tooltip content={enabled ? 'Play it now' : 'Sound is switched off'} side="top">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={`Preview the sound for “${meta.title}”`}
              disabled={!enabled}
              onClick={() => preview(event)}
            >
              <Play aria-hidden="true" />
            </Button>
          </Tooltip>

          <Tooltip content="Upload for every console" side="top">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              loading={uploading === 'fleet'}
              disabled={uploading !== null}
              aria-label={`Upload a sound for “${meta.title}” for every console`}
              onClick={() => pick('fleet')}
            >
              <Upload aria-hidden="true" />
            </Button>
          </Tooltip>

          <Tooltip content="Upload for this browser only" side="top">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              loading={uploading === 'local'}
              disabled={uploading !== null}
              aria-label={`Upload a sound for “${meta.title}” for this browser only`}
              onClick={() => pick('local')}
            >
              <Laptop aria-hidden="true" />
            </Button>
          </Tooltip>
          <input
            ref={fileRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={(e) => {
              void onFile(e.target.files?.[0])
              // Cleared so choosing the same file again still fires `change`.
              e.target.value = ''
            }}
          />
        </div>
      </div>
    </SettingCard>
  )
}
